/**
 * Chat / operator widgets live in cross-origin iframes, so we never see the
 * conversation itself. Most of them, however, notify the host page about key
 * moments via `window.postMessage`, and postMessage is broadcast to every
 * `message` listener on the window — including us. No widget-side integration
 * is needed.
 *
 * Adapters (add more as clients bring new widgets):
 *   - b24u (Bitrix24 wrapper, i.b24u.ru): `{source: 'b24u_chat', type, payload}`
 *       chat_first_message                         → chat_message
 *       lead_created / phone_received /
 *       contact_received / email_received /
 *       chat_message_lead                          → chat_lead {kind}
 *
 *   - Jivo (code.jivosite.com): no postMessage for the host page. Jivo calls
 *     global functions the site may define (`jivo_onMessageSent`, ...), so we
 *     put our own function under each name and keep calling the site's one:
 *       jivo_onMessageSent / jivo_onClientStartChat → chat_message
 *       jivo_onIntroduction                          → chat_lead {kind: introduction}
 *       jivo_onCallStart                             → chat_lead {kind: call_start}
 *     Installed only when a Jivo script or its API object is on the page.
 *
 * Emitted props never include the payload itself (it can carry a phone or
 * email) — only provider and kind.
 */
import type { Json } from "../types";

export interface ChatWidgetHookOptions {
  emit: (eventName: string, props: Record<string, Json>) => void;
}

const B24U_LEAD_TYPES = new Set([
  "lead_created",
  "phone_received",
  "contact_received",
  "email_received",
  "chat_message_lead",
]);

export function installChatWidgetHooks(opts: ChatWidgetHookOptions): () => void {
  // One chat_message per page load is enough: the widget itself only fires
  // chat_first_message once per conversation, this just guards against replays.
  let messageEmitted = false;
  const leadKindsEmitted = new Set<string>();

  function onMessage(ev: MessageEvent) {
    const data = ev && (ev.data as { source?: unknown; type?: unknown } | null);
    if (!data || typeof data !== "object") return;

    if (data.source === "b24u_chat" && typeof data.type === "string") {
      if (data.type === "chat_first_message") {
        if (messageEmitted) return;
        messageEmitted = true;
        opts.emit("chat_message", { provider: "b24u" });
        return;
      }
      if (B24U_LEAD_TYPES.has(data.type)) {
        if (leadKindsEmitted.has(data.type)) return;
        leadKindsEmitted.add(data.type);
        opts.emit("chat_lead", { provider: "b24u", kind: data.type });
      }
    }
  }

  window.addEventListener("message", onMessage);
  const stopJivo = installJivoHooks(opts);
  return () => {
    window.removeEventListener("message", onMessage);
    stopJivo();
  };
}

const JIVO_WRAPPED = Symbol.for("xpixel.jivo.wrapped");
const JIVO_MESSAGE_CALLBACKS = ["jivo_onMessageSent", "jivo_onClientStartChat"];
const JIVO_LEAD_CALLBACKS: Record<string, string> = {
  jivo_onIntroduction: "introduction",
  jivo_onCallStart: "call_start",
};
// The pixel can load before Jivo (and before the site declares its own
// callbacks): keep checking for a while and re-wrap whatever appears.
const JIVO_POLL_MS = 1000;
const JIVO_POLL_ATTEMPTS = 60;

type JivoFn = ((...args: unknown[]) => unknown) & { [JIVO_WRAPPED]?: true };

function jivoPresent(): boolean {
  const w = window as unknown as Record<string, unknown>;
  if (w.jivo_api || w.jivo_config) return true;
  return document.querySelector('script[src*="jivosite"], script[src*="jivo.ru"], jdiv') !== null;
}

function installJivoHooks(opts: ChatWidgetHookOptions): () => void {
  let messageEmitted = false;
  const leadKindsEmitted = new Set<string>();

  function fire(name: string): void {
    try {
      if (JIVO_MESSAGE_CALLBACKS.indexOf(name) !== -1) {
        if (messageEmitted) return;
        messageEmitted = true;
        opts.emit("chat_message", { provider: "jivo" });
        return;
      }
      const kind = JIVO_LEAD_CALLBACKS[name];
      if (kind && !leadKindsEmitted.has(kind)) {
        leadKindsEmitted.add(kind);
        opts.emit("chat_lead", { provider: "jivo", kind });
      }
    } catch {
      // never throw out of pixel
    }
  }

  function wrapAll(): void {
    const w = window as unknown as Record<string, JivoFn | undefined>;
    const names = JIVO_MESSAGE_CALLBACKS.concat(Object.keys(JIVO_LEAD_CALLBACKS));
    for (const name of names) {
      const current = w[name];
      if (typeof current === "function" && current[JIVO_WRAPPED]) continue;
      const original = typeof current === "function" ? current : null;
      const wrapped: JivoFn = function (this: unknown, ...args: unknown[]) {
        fire(name);
        return original ? original.apply(this, args) : undefined;
      };
      wrapped[JIVO_WRAPPED] = true;
      try {
        w[name] = wrapped;
      } catch {
        // a read-only global: leave the site's function alone
      }
    }
  }

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    if (jivoPresent()) wrapAll();
    if (attempts >= JIVO_POLL_ATTEMPTS) window.clearInterval(timer);
  }, JIVO_POLL_MS);
  if (jivoPresent()) wrapAll();
  return () => window.clearInterval(timer);
}
